import React, { useCallback, useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { CloudOffRounded } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  sendMessage,
  checkHealth,
  checkBotAccess,
  fetchHistory,
  clearError,
  appendPendingBotMessage,
  setNewThreadPending,
} from "../../../shared/chatBot/chatBotSlice";
import { POPUP_THREAD_ID } from "../../../shared/chatBot/models/chatBotModels";
import { fetchThreads, openArtifact } from "../midasPageSlice";
import { Artifact } from "../midasPageModels";
import ChatMessageList from "../../../shared/chatBot/components/ChatMessageList";
import ChatInputArea from "../../../shared/chatBot/components/ChatInputArea";
import MidasChatHeader from "./MidasChatHeader";
import MidasWelcome from "./MidasWelcome";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

// ===================================================================
//  MidasChatArea -- reuses existing chat components + welcome screen
//  F6.9: Bot offline empty state
//  F6.7: Reduced motion propagation
// ===================================================================

interface MidasChatAreaProps {
  prefersReducedMotion?: boolean;
}

const MidasChatArea: React.FC<MidasChatAreaProps> = ({ prefersReducedMotion = false }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const messages = useAppSelector((state) => state.chatBot.messages);
  const machineContext = useAppSelector((state) => state.chatBot.machineContext);
  const user = useAppSelector((state) => state.account.user);
  const artifactPanelOpen = useAppSelector((state) => state.midasPage.artifactPanelOpen);
  const activeThreadId = useAppSelector((state) => state.midasPage.activeThreadId);
  const isBotAvailable = useAppSelector((state) => state.chatBot.isBotAvailable);
  const threadDetailLoading = useAppSelector((state) => state.midasPage.threadDetailLoading);
  const isPhone = useMediaQuery("(max-width:768px)");

  // MIDAS-SCOPESTREAM: Read isLoading from the active thread's buffer.
  // Falls back to the popup buffer on mount if a popup stream was in flight (adoption).
  const effectiveThreadId = activeThreadId || POPUP_THREAD_ID;
  const threadBuffer = useAppSelector((state) =>
    state.chatBot.threadBuffers[effectiveThreadId]
  );
  const popupBuffer = useAppSelector((state) =>
    state.chatBot.threadBuffers[POPUP_THREAD_ID]
  );
  // On MIDAS mount, check if a popup stream is active or completed — adopt it
  const adoptingPopup = !activeThreadId && (popupBuffer?.isLoading || popupBuffer?.pendingBotMessage);
  const isLoading = adoptingPopup
    ? (popupBuffer?.isLoading || false)
    : (threadBuffer?.isLoading || false);

  const hasMessages = messages.length > 0;

  // Shrink max-width when artifact panel is open
  const chatMaxWidth = artifactPanelOpen ? "100%" : "800px";

  // Initialize bot state on mount (in case ChatWidget hasn't loaded yet).
  // BUG-2026-03-20 FIX: Skip fetchHistory() when a thread is active or loading,
  // because fetchHistory resolves asynchronously and its .fulfilled handler
  // overwrites state.messages — wiping out the thread messages that loadThread
  // just loaded. fetchHistory is only needed for the "current session" (no thread).
  useEffect(() => {
    if (user) {
      dispatch(checkBotAccess());
      dispatch(checkHealth());
      if (!activeThreadId && !threadDetailLoading) {
        dispatch(fetchHistory());
      }
      dispatch(fetchThreads());
    }
  }, [dispatch, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // MIDAS-SCOPESTREAM: Popup adoption on mount.
  // If a popup stream completed during navigation (pendingBotMessage exists), append it.
  useEffect(() => {
    if (popupBuffer && !popupBuffer.isLoading && popupBuffer.pendingBotMessage) {
      dispatch(appendPendingBotMessage({ threadId: POPUP_THREAD_ID }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // MIDAS-SCOPESTREAM: When a stream completes (pendingBotMessage appears in the buffer),
  // append it to messages. Covers both:
  // - Flow A: user stayed on the same thread (pendingBotMessage set by fulfilled)
  // - Flow B: user switched away and back (activeThreadId changes, loadThread fires,
  //   but also caught here if loadThread didn't handle it)
  const hasPendingMsg = threadBuffer?.pendingBotMessage;
  useEffect(() => {
    if (activeThreadId && threadBuffer && !threadBuffer.isLoading && threadBuffer.pendingBotMessage) {
      dispatch(appendPendingBotMessage({ threadId: activeThreadId }));
    }
  }, [activeThreadId, hasPendingMsg, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = useCallback(
    (message: string, image?: File) => {
      // Clear any previous error so the input doesn't stay frozen
      dispatch(clearError());
      // BUG-FIX: If sending from the welcome screen (no active thread), ensure
      // newThreadPending is set so sendMessage follows the optimistic thread path.
      // Without this, fresh-load → direct send skips addOptimisticThread and the
      // stream goes to POPUP_THREAD_ID with no activeThreadId to display it.
      if (!activeThreadId) {
        dispatch(setNewThreadPending());
      }
      dispatch(
        sendMessage({
          message,
          machineId: machineContext?.machineId,
          threadId: activeThreadId || undefined,
          image,
          // MIDAS-SCOPESTREAM: Scope streaming to the active thread
          originThreadId: activeThreadId || undefined,
        })
      );
    },
    [dispatch, machineContext, activeThreadId]
  );

  const handleSuggestionClick = useCallback(
    (text: string) => {
      handleSend(text);
    },
    [handleSend]
  );

  const handleImageClick = useCallback(
    (imageUrl: string, imageName: string) => {
      const artifact: Artifact = {
        type: "image",
        title: imageName,
        source: "machineid",
        url: imageUrl,
      };
      dispatch(openArtifact(artifact));
    },
    [dispatch]
  );

  // F6.9: Bot offline empty state
  const showBotOffline = isBotAvailable === false && !hasMessages && !isLoading;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0, // Allow flex shrinking
        overflow: "hidden",
        backgroundColor: dt.pageBg,
      }}
    >
      <MidasChatHeader />

      {/* F6.9: Bot offline state */}
      {showBotOffline ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            padding: "40px 24px",
          }}
        >
          <CloudOffRounded
            sx={{ fontSize: "48px", color: dt.textMuted, opacity: 0.5 }}
          />
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "16px",
              fontWeight: 600,
              color: dt.textSecondary,
              textAlign: "center",
            }}
          >
            {translate("midas.emptyState.botOfflineTitle")}
          </Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textMuted,
              textAlign: "center",
              maxWidth: "360px",
              lineHeight: 1.5,
            }}
          >
            {translate("midas.emptyState.botOfflineDesc")}
          </Box>
        </Box>
      ) : !hasMessages && !isLoading && !threadDetailLoading ? (
        /* Welcome screen — also hidden while a thread is loading (BUG-2026-03-20) */
        <Box
          sx={{
            flex: 1,
            display: "flex",
            overflow: "auto",
            background: dt.pageBg,
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(52,152,219,0.04) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        >
          <MidasWelcome onSuggestionClick={handleSuggestionClick} />
        </Box>
      ) : (
        <>
          {/* Chat content area with centered max-width */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              maxWidth: chatMaxWidth,
              width: "100%",
              margin: "0 auto",
              transition: prefersReducedMotion ? "none" : "max-width 280ms ease-out",
            }}
          >
            <ChatMessageList
              onSuggestionClick={handleSuggestionClick}
              isPhone={isPhone}
              onImageClick={handleImageClick}
              threadId={adoptingPopup ? POPUP_THREAD_ID : (activeThreadId || undefined)}
            />
          </Box>
        </>
      )}

      {/* Input area with centered max-width */}
      <Box
        sx={{
          maxWidth: chatMaxWidth,
          width: "100%",
          margin: "0 auto",
          transition: prefersReducedMotion ? "none" : "max-width 280ms ease-out",
        }}
      >
        <ChatInputArea
          onSend={handleSend}
          disabled={isLoading || isBotAvailable === false}
          imageUploadDisabled={!machineContext}
          variant="capsule"
        />
      </Box>
    </Box>
  );
};

export default MidasChatArea;
